/**
 * Context Collection APIs for Electron
 * Handles accessibility permissions, context collection, and observer management
 */
import { type ContextCollectionStatus } from '@electron/shared';

import { getEnvVar } from '@/env';
import { ACCESS_TOKEN_LOCAL_STORAGE_KEY } from '@/utils/constants';
import { getLocalStorageItem } from '@/utils/localStorage';

import { ElectronAPI } from '../base/ElectronAPI';
import { type ElectronListenerMethod, IPCEventType } from '../base/types';

const API_URL = getEnvVar('VITE_API_URL');
type ContextCollectionActionResult = { success: boolean; error?: string };

function assertContextCollectionActionSucceeded(
  result: ContextCollectionActionResult | undefined,
  fallbackMessage: string
) {
  if (!result?.success) {
    if (result?.error) {
      console.error('Context collection action failed:', result.error);
    }
    throw new Error(fallbackMessage);
  }
}

export class ContextAPI extends ElectronAPI {
  /**
   * Start context collection - calls Swift ContextKit
   */
  async startContextCollection(email: string, id: string, isInternal: boolean) {
    const appVersion = await this.mainProcessApi.getAppVersion();
    const accessToken = getLocalStorageItem(ACCESS_TOKEN_LOCAL_STORAGE_KEY);
    const observerPayload = {
      token: accessToken,
      url: API_URL,
      appVersion: appVersion,
    };
    await this.notifyContextKit(IPCEventType.setTokenAndUrl, observerPayload);
    await this.notifyContextKit(IPCEventType.setUserEmail, {
      email,
      id,
      isInternal,
    });
    // await this.notifyContextKit(IPCEventType.runObserver, {});
    const result = await this.invoke<ContextCollectionActionResult>(
      'start-context-collection',
      {}
    );
    assertContextCollectionActionSucceeded(
      result,
      'Failed to start Context Awareness'
    );
  }

  /**
   * Stop context collection - calls Swift ContextKit
   */
  async stopContextCollection() {
    const result = await this.invoke<ContextCollectionActionResult>(
      'stop-context-collection',
      {}
    );
    assertContextCollectionActionSucceeded(
      result,
      'Failed to stop Context Awareness'
    );
    // return this.notifyContextKit(IPCEventType.stopObserver, {});
  }

  /**
   * Check if context collection is working - calls Swift ContextKit
   */
  async isContextCollectionWorking() {
    return this.requestContextKit(IPCEventType.isObserverRunning, {})
      .then((response) => response.isRunning ?? false)
      .catch(() => false);
  }

  /**
   * Check accessibility permissions - calls Electron directly via node-mac-permissions
   */
  async checkAccessibilityPermissions() {
    try {
      return await this.mainProcessApi.checkAccessibilityPermission();
    } catch (error) {
      console.error('Error checking accessibility permissions:', error);
      return false;
    }
  }

  /**
   * Request accessibility permissions - calls Electron directly via node-mac-permissions
   */
  async requestAccessibilityPermissions(): Promise<boolean> {
    try {
      return await this.mainProcessApi.requestAccessibilityPermission();
    } catch (error) {
      console.error('Error requesting accessibility permissions:', error);
      return false;
    }
  }

  /**
   * Check screen recording permissions - calls Electron directly via node-mac-permissions
   */
  async checkScreenRecordingPermission(): Promise<boolean> {
    try {
      return await this.mainProcessApi.checkScreenRecordingPermission();
    } catch (error) {
      console.error('Error checking screen recording permission:', error);
      return false;
    }
  }

  /**
   * Request screen recording permissions - opens System Preferences via node-mac-permissions
   */
  async requestScreenRecordingPermission(): Promise<void> {
    try {
      await this.mainProcessApi.requestScreenRecordingPermission();
    } catch (error) {
      console.error('Error requesting screen recording permission:', error);
    }
  }

  /**
   * Get current context collection status from ContextCollectionManager
   */
  async getContextCollectionStatus() {
    return this.invoke<ContextCollectionStatus>(
      'get-context-collection-status'
    );
  }

  /**
   * Force a context collection status check
   */
  async forceContextCollectionStatusCheck() {
    return this.invoke('force-context-collection-status-check');
  }

  /**
   * Pause context collection for specified minutes
   */
  async pauseContextCollection(minutes: number) {
    const result = await this.invoke<ContextCollectionActionResult>(
      'pause-context-collection',
      minutes
    );
    assertContextCollectionActionSucceeded(
      result,
      'Failed to pause Context Awareness'
    );
  }

  /**
   * Disable context collection until the user enables it again
   */
  async disableContextCollection() {
    const result = await this.invoke<ContextCollectionActionResult>(
      'disable-context-collection'
    );
    assertContextCollectionActionSucceeded(
      result,
      'Failed to disable Context Awareness'
    );
  }

  /**
   * Resume context collection immediately
   */
  async resumeContextCollection() {
    const result = await this.invoke<ContextCollectionActionResult>(
      'resume-context-collection'
    );
    assertContextCollectionActionSucceeded(
      result,
      'Failed to resume Context Awareness'
    );
  }

  async settleContextCollectionConsent() {
    const result = await this.invoke<ContextCollectionActionResult>(
      'settle-context-collection-consent'
    );
    assertContextCollectionActionSucceeded(
      result,
      'Failed to record Context Awareness consent'
    );
  }

  async resetContextCollectionConsent() {
    const result = await this.invoke<ContextCollectionActionResult>(
      'reset-context-collection-consent'
    );
    assertContextCollectionActionSucceeded(
      result,
      'Failed to reset Context Awareness consent'
    );
  }

  /**
   * Delete context collected after specified timestamp
   * @param timestampMs - Unix timestamp in milliseconds. Context collected at and after this timestamp will be deleted.
   *                      Pass null or undefined to delete all context.
   */
  async deleteContext(timestampMs?: number, endTimestampMs?: number) {
    return this.notifyContextKit(IPCEventType.deleteContext, {
      deleteFrom: timestampMs ?? undefined,
      deleteTo: endTimestampMs,
    });
  }

  /** Register a callback for when ContextKit crashes. Returns an unlisten function. */
  listenForContextKitDidCrash: ElectronListenerMethod<IPCEventType.contextKitDidCrash> =
    (callback) => {
      return this.addListener({
        eventType: IPCEventType.contextKitDidCrash,
        callback,
      });
    };

  /** Register a callback for when ContextKit recovers after a crash. Returns an unlisten function. */
  listenForContextKitDidRecover: ElectronListenerMethod<IPCEventType.contextKitDidRecover> =
    (callback) => {
      return this.addListener({
        eventType: IPCEventType.contextKitDidRecover,
        callback,
      });
    };

  async getMentionableWindows() {
    const response = await this.requestContextKit(
      IPCEventType.getMentionableWindows,
      {}
    );
    return response.windows ?? [];
  }

  async getCapturableWindows() {
    const response = await this.requestContextKit(
      IPCEventType.getCapturableWindows,
      {}
    );
    return response.windows ?? [];
  }

  /**
   * Capture screenshot of a window by its window ID
   * @param windowId - CGWindowID of the window to capture
   */
  async captureAppScreenshot(windowId: number) {
    return this.requestContextKit(IPCEventType.captureAppScreenshot, {
      windowId,
    });
  }

  /**
   * Get the current front app context (on-demand pull model)
   * Returns the context of the frontmost application including window title and focused element
   */
  async getFrontAppContext() {
    try {
      return await this.requestContextKit(IPCEventType.getFrontAppContext, {});
    } catch {
      return null;
    }
  }

  /**
   * Paste text into the currently focused element of the front app
   * Uses clipboard-based pasting with automatic clipboard restoration
   */
  async pasteText(text: string): Promise<boolean> {
    try {
      await this.requestContextKit(IPCEventType.pasteText, { text });
      return true;
    } catch {
      return false;
    }
  }

  /**
   * Request a fresh snapshot from ContextKit
   */
  triggerManualSnapshot() {
    this.notifyContextKit(IPCEventType.manualSnapshot, {});
  }

  /**
   * Enable the debug snapshot report stream from ContextKit
   */
  enableDebugStream() {
    this.notifyContextKit(IPCEventType.enableDebugStream, {});
  }

  /**
   * Disable the debug snapshot report stream from ContextKit
   */
  disableDebugStream() {
    this.notifyContextKit(IPCEventType.disableDebugStream, {});
  }

  /**
   * Listen for debug snapshot reports from ContextKit
   */
  listenForDebugSnapshotReport: ElectronListenerMethod<IPCEventType.debugSnapshotReport> =
    (callback) => {
      return this.addListener({
        eventType: IPCEventType.debugSnapshotReport,
        callback,
      });
    };
}
